import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { getPaymentRequest } from 'services/paymentRequestsApi';
import type { PaymentRequest } from 'types/paymentRequests';

export type PaymentRequestLoadError = {
    message: string;
    status: number | null;
};

interface WalletInvoiceDetailState {
    request: PaymentRequest | null;
    loading: boolean;
    error: PaymentRequestLoadError | null;
}

const initialState: WalletInvoiceDetailState = {
    request: null,
    loading: false,
    error: null,
};

/**
 * Load a single payment request. Errors stay in-slice (in-page Alert) — no toast meta —
 * so a blank TransactionDetails card is never shown on failure.
 * Backend GET returns 404 for missing *and* unauthorized (not sender/recipient); not 403.
 */
export const fetchWalletInvoiceDetail = createAsyncThunk<PaymentRequest, string, { rejectValue: PaymentRequestLoadError }>(
    'walletInvoiceDetail/fetchWalletInvoiceDetail',
    async (id, { rejectWithValue }) => {
        try {
            return await getPaymentRequest(id);
        } catch (err) {
            const status =
                typeof (err as { response?: { status?: number } })?.response?.status === 'number'
                    ? (err as { response: { status: number } }).response.status
                    : null;
            const message =
                getProblemDetail(err) ||
                ((err as { message?: string })?.message === 'Network Error' ? 'Network Error' : 'Failed to load payment request');
            return rejectWithValue({ message, status });
        }
    }
);

const walletInvoiceDetailSlice = createSlice({
    name: 'walletInvoiceDetail',
    initialState,
    reducers: {
        clearWalletInvoiceDetail(state) {
            state.request = null;
            state.error = null;
            state.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchWalletInvoiceDetail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchWalletInvoiceDetail.fulfilled, (state, action) => {
            state.loading = false;
            state.request = action.payload;
            state.error = null;
        });
        builder.addCase(fetchWalletInvoiceDetail.rejected, (state, action) => {
            state.loading = false;
            state.request = null;
            state.error = action.payload || {
                message: action.error.message || 'Failed to load payment request',
                status: null,
            };
        });
    },
});

export const { clearWalletInvoiceDetail } = walletInvoiceDetailSlice.actions;
export default walletInvoiceDetailSlice.reducer;
